import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, XCircle, Award, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QcmPage() {
  const { lookup } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) { nav("/connexion", { state: { from: `/formation/${lookup}/qcm` } }); return; }
    axios.get(`${API}/lms/quiz/${lookup}`)
      .then(({ data }) => setQuiz(data))
      .catch((e) => setError(e?.response?.data?.detail || "QCM indisponible."));
  }, [lookup, user]);

  const allAnswered = quiz && quiz.questions.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    if (!allAnswered) return toast.error("Répondez à toutes les questions.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/lms/quiz/${lookup}/submit`, { answers });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="[ Évaluation finale ]"
        titleLines={[quiz?.title || "QCM", "Final."]}
        intro={quiz ? `Tentative ${quiz.attempt_number}${quiz.max_attempts ? ` sur ${quiz.max_attempts}` : ""} — seuil de réussite : ${quiz.passing_score} %. Le score est calculé côté serveur.` : ""}
      />
      <section className="bg-paper pb-28" data-testid="qcm-page">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          {error && (
            <div className="rounded-sm border hairline bg-white p-10 text-center">
              <XCircle size={44} className="text-ink/25 mx-auto" />
              <p className="text-ink/60 mt-4">{error}</p>
              <Link to="/mon-espace" className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 label-mono hover:bg-gold hover:text-ink transition-colors mt-6">Mon espace</Link>
            </div>
          )}

          {!error && !quiz && <div className="text-center py-20"><Loader2 size={30} className="animate-spin text-gold mx-auto" /></div>}

          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-sm border hairline bg-white p-8 md:p-12 text-center mb-10" data-testid="qcm-result">
              {result.passed ? (
                <>
                  <Award size={56} className="text-gold mx-auto" />
                  <span className="label-mono text-gold block mt-4">Formation réussie</span>
                  <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3">Félicitations !</h2>
                </>
              ) : (
                <>
                  <XCircle size={56} className="text-red-400 mx-auto" />
                  <span className="label-mono text-red-500 block mt-4">QCM non validé</span>
                  <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3">Encore un effort.</h2>
                </>
              )}
              <div className="font-display text-6xl font-semibold text-ink mt-6" data-testid="qcm-score">{result.percent} %</div>
              <p className="text-ink/55 mt-2">Score : {result.score} / {result.total} — seuil de réussite : {result.passing_score} %</p>
              {result.passed && result.certificate && (
                <div className="mt-8 rounded-sm border border-gold/40 bg-gold/10 p-6">
                  <ShieldCheck size={22} className="text-gold mx-auto" />
                  <p className="label-mono text-ink/60 mt-3">Votre certificat a été généré automatiquement</p>
                  <p className="font-mono-tech text-lg font-semibold mt-1" data-testid="qcm-cert-number">{result.certificate.certificate_number}</p>
                  <div className="flex flex-wrap gap-3 justify-center mt-5">
                    <a href={`${API}/certificates/${result.certificate.certificate_number}/pdf`} data-testid="qcm-cert-download"
                      className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-3 label-mono hover:bg-ink hover:text-paper transition-colors">Télécharger le PDF</a>
                    <Link to="/mon-espace" className="inline-flex items-center gap-2 rounded-full border hairline px-6 py-3 label-mono hover:bg-ink hover:text-paper transition-colors">Mes certificats</Link>
                  </div>
                </div>
              )}
              {!result.passed && result.attempts_left !== null && result.attempts_left > 0 && (
                <button onClick={() => { setResult(null); setAnswers({}); window.location.reload(); }} data-testid="qcm-retry"
                  className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 label-mono hover:bg-gold hover:text-ink transition-colors mt-8">
                  <RotateCcw size={14} /> Repasser le QCM ({result.attempts_left} tentative{result.attempts_left > 1 ? "s" : ""} restante{result.attempts_left > 1 ? "s" : ""})
                </button>
              )}
              {!result.passed && result.attempts_left === 0 && (
                <p className="text-ink/50 mt-6">Nombre maximum de tentatives atteint. Contactez NovaLean Consulting.</p>
              )}
            </motion.div>
          )}

          {quiz && !result && (
            <div className="space-y-5">
              {quiz.questions.map((q, qi) => (
                <motion.div key={q.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: qi * 0.05 }}
                  className="rounded-sm border hairline bg-white p-6 md:p-8" data-testid={`qcm-question-${qi}`}>
                  <span className="label-mono text-gold">Question {qi + 1} / {quiz.questions.length}</span>
                  <h3 className="font-display text-xl md:text-2xl font-semibold mt-3 leading-snug">{q.question}</h3>
                  <div className="grid sm:grid-cols-2 gap-3 mt-5">
                    {q.options.map((opt, oi) => (
                      <button key={oi} onClick={() => setAnswers({ ...answers, [q.id]: oi })} data-testid={`qcm-q${qi}-opt${oi}`}
                        className={`text-left rounded-sm border px-4 py-3.5 text-sm transition-colors ${answers[q.id] === oi ? "border-gold bg-gold/10 text-ink font-medium" : "hairline text-ink/70 hover:border-ink/40"}`}>
                        <span className="label-mono text-ink/40 mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ))}
              <button onClick={submit} disabled={!allAnswered || busy} data-testid="qcm-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-50 mt-4">
                {busy ? <Loader2 size={15} className="animate-spin" /> : "Valider mes réponses"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <span className="label-mono text-ink/50">Correction détaillée</span>
              {result.correction.map((c, i) => (
                <div key={c.id} className={`rounded-sm border p-5 ${c.correct ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/40"}`} data-testid={`qcm-correction-${i}`}>
                  <div className="flex items-start gap-3">
                    {c.correct ? <CheckCircle2 size={18} className="text-green-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-500 shrink-0 mt-0.5" />}
                    <div>
                      <p className="font-medium text-ink">{c.question}</p>
                      <p className="text-sm mt-1.5 text-ink/60">
                        Votre réponse : <span className={c.correct ? "text-green-700" : "text-red-600 line-through"}>{c.chosen !== null && c.chosen !== undefined ? c.options[c.chosen] : "—"}</span>
                        {!c.correct && <> · Bonne réponse : <span className="text-green-700">{c.options[c.correct_index]}</span></>}
                      </p>
                      {c.explanation && <p className="text-sm text-ink/50 mt-1 italic">{c.explanation}</p>}
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/mon-espace" className="inline-flex items-center gap-2 label-mono text-ink hover:text-gold transition-colors mt-4"><ArrowLeft size={14} /> Retour à mon espace</Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
